import React, { useState, useEffect } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import GraphqlService from '../../service/graphqlService';
import { AUDIT_ENTITY_CERTIFICATE_PROVISIONING, AUDIT_CHANGE_CREDENTIAL_DELETED } from '../../hooks/useAuditLog';
import useAnalytics from '../../hooks/useAnalytics';

/**
 * DeleteCredentialModal - Two-step confirmation modal for permanently deleting credentials
 * 
 * Step 1: If credential has access keys, warns user and deletes them first
 * Step 2: Permanently deletes the physical user
 * 
 * This action is IRREVERSIBLE - credentials cannot be recovered after deletion.
 */
const DeleteCredentialModal = ({ isOpen, credential, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accessKeysDeleted, setAccessKeysDeleted] = useState(false);
  
  const { setAnalytics } = useAnalytics();

  // Track modal open event
  useEffect(() => {
    if (isOpen && credential) {
      setAnalytics({
        event: 'delete_credential_modal_opened',
        credentialId: credential.id,
        hasAccessKeys: !!credential?.accessKeys?.[0]?.accessKeyId,
      });
    }
  }, [isOpen, credential, setAnalytics]);

  if (!isOpen) return null;

  const hasAccessKeys = credential?.accessKeys?.[0]?.accessKeyId;

  const handleDeleteAccessKeys = async () => {
    setIsLoading(true);
    setError(null);
    
    setAnalytics({
      event: 'delete_access_keys_initiated',
      credentialId: credential.id,
    });
    
    try {
      // First, fetch the physical user details to get the accessKeyId
      // The list endpoint (getPhysicalUsers) doesn't return accessKeys,
      // but the single user endpoint (getPhysicalUser) does
      const userDetails = await GraphqlService.getPhysicalUser(credential.id);
      const accessKeyId = userDetails?.accessKeys?.[0]?.accessKeyId;
      
      if (!accessKeyId) {
        // No access keys to delete, proceed directly to step 2
        setAccessKeysDeleted(true);
        setStep(2);
        setAnalytics({
          event: 'delete_access_keys_skipped',
          credentialId: credential.id,
          reason: 'no_access_keys_found',
        });
        return;
      }
      
      await GraphqlService.deleteAccessKeys(credential.id, accessKeyId);
      setAccessKeysDeleted(true);
      setStep(2);
      
      setAnalytics({
        event: 'delete_access_keys_success',
        credentialId: credential.id,
      });
    } catch (err) {
      console.error('Error deleting access keys:', err);
      setError(`Failed to delete access keys: ${err.message}`);
      
      setAnalytics({
        event: 'delete_access_keys_error',
        credentialId: credential.id,
        errorMessage: err.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePhysicalUser = async () => {
    setIsLoading(true);
    setError(null);
    
    setAnalytics({
      event: 'delete_credential_initiated',
      credentialId: credential.id,
      accessKeysWereDeleted: accessKeysDeleted,
    });
    
    try {
      await GraphqlService.deletePhysicalUserPermanent(credential.id);

      setAnalytics({
        event: 'delete_credential_success',
        credentialId: credential.id,
        accessKeysWereDeleted: accessKeysDeleted,
      });

      onSuccess({
        success: true,
        credential: credential,
        auditInfo: {
          entity: AUDIT_ENTITY_CERTIFICATE_PROVISIONING,
          change: AUDIT_CHANGE_CREDENTIAL_DELETED,
          entityId: credential.id,
          description: 'Credential Permanently Deleted',
          logicalUser: credential.logicalUser || credential.id,
          value: JSON.stringify({ deleted: true, accessKeysDeleted })
        }
      });
    } catch (err) {
      console.error('Error deleting physical user:', err);
      setError(`Failed to delete credential: ${err.message}`);
      
      setAnalytics({
        event: 'delete_credential_error',
        credentialId: credential.id,
        errorMessage: err.message,
        accessKeysWereDeleted: accessKeysDeleted,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (step === 1) {
      // Step 1: Always call deleteAccessKeys first (PUT endpoint)
      // The backend will fetch accessKeyId if not provided
      await handleDeleteAccessKeys();
    } else {
      // Step 2: Delete the physical user (DELETE endpoint)
      await handleDeletePhysicalUser();
    }
  };

  const handleClose = () => {
    // Track cancellation
    setAnalytics({
      event: 'delete_credential_modal_cancelled',
      credentialId: credential?.id,
      step: step,
      accessKeysWereDeleted: accessKeysDeleted,
    });
    
    // Reset state when closing
    setStep(1);
    setError(null);
    setAccessKeysDeleted(false);
    onClose();
  };

  const renderStepContent = () => {
    if (error) {
      return (
        <div className="mb-6">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600">
            {accessKeysDeleted 
              ? 'Access keys were deleted but the credential could not be removed. Please try again or contact support.'
              : 'Please try again or contact support if the problem persists.'}
          </p>
        </div>
      );
    }

    if (hasAccessKeys && step === 1) {
      return (
        <div className="mb-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Warning: Access Keys Detected</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  This credential has associated access keys that must be deleted first.
                </p>
              </div>
            </div>
          </div>
          <p className="text-gray-700">
            <strong>Step 1 of 2:</strong> Delete the access keys for this credential.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            After the access keys are deleted, you will be prompted to confirm the permanent deletion of the credential.
          </p>
        </div>
      );
    }

    // Step 1 for credentials without detected access keys (still need to call PUT first)
    if (step === 1) {
      return (
        <div className="mb-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Two-Step Deletion Required</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  To delete this credential, we must first clean up any associated access keys.
                </p>
              </div>
            </div>
          </div>
          <p className="text-gray-700">
            <strong>Step 1 of 2:</strong> Clean up access keys for this credential.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            After cleanup, you will be prompted to confirm the permanent deletion of the credential.
          </p>
        </div>
      );
    }

    return (
      <div className="mb-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Permanent Deletion</h3>
              <p className="text-sm text-red-700 mt-1">
                This action cannot be undone. The credential will be permanently removed.
              </p>
            </div>
          </div>
        </div>
        <p className="text-gray-700">
          {hasAccessKeys && accessKeysDeleted 
            ? <><strong>Step 2 of 2:</strong> Access keys have been deleted. Confirm permanent deletion of the credential.</>
            : <>Are you sure you want to permanently delete this credential?</>
          }
        </p>
        <div className="mt-4 bg-gray-50 rounded-md p-3">
          <p className="text-sm text-gray-600">
            <strong>Credential ID:</strong> {credential?.id}
          </p>
          <p className="text-sm text-gray-600">
            <strong>Description:</strong> {credential?.description || 'N/A'}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md mx-auto">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          {error ? 'Deletion Failed' : 'Delete Credential'}
        </h2>
        
        {renderStepContent()}

        {isLoading ? (
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-500" />
            <p className="ml-2 text-gray-700">
              {hasAccessKeys && step === 1 ? 'Deleting access keys...' : step === 1 ? 'Cleaning up...' : 'Deleting credential...'}
            </p>
          </div>
        ) : (
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              {error 
                ? 'Retry' 
                : step === 1 
                  ? (hasAccessKeys ? 'Delete Access Keys' : 'Clean Up & Continue')
                  : 'Delete Permanently'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteCredentialModal;
